import {
  type EnrollmentJointAccountTypeSubmitInput,
  type JointEnrollmentAccountType,
  useGetEnrollmentJointAccountTypeData,
  useSubmitEnrollmentJointAccountTypeData,
} from '@egw/api'
import { Button, Alert, AlertType, GenericError, RadioGroup, ProgressBar, ModalDrawer, Tag } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { setContext } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { UserIcon } from '@heroicons/react/16/solid'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { type FC, useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { PageHeader } from '../../../components'
import { JOINT_ACCOUNT_TYPE } from '../../../constants/joint-account-types'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'

const log = new Logger('Enrollment - Joint - AccountType')

interface FormData {
  accountType:
    | 'JOINT_TENANTS_WITH_RIGHT_OF_SURVIVORSHIP'
    | 'TENANTS_IN_COMMON'
    | 'JOINT_COMMUNITY_PROPERTY'
    | 'JOINT_TENANTS_BY_THE_ENTIRETY'
    | undefined
}

export const AccountTypePageV2: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const [isJtwrosModalOpen, setIsJtwrosModalOpen] = useState(false) // Type 1
  const [isTicModalOpen, setIsTicModalOpen] = useState(false) // Type 2
  const [isCommunityPropertyModalOpen, setIsCommunityPropertyModalOpen] = useState(false) // Type 3
  const [isJointTenantsEntiretyModalOpen, setIsJointTenantsEntiretyModalOpen] = useState(false) // Type 4

  const { data, isLoading, error } = useGetEnrollmentJointAccountTypeData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointAccountTypeData()

  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - AccountType')

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      accountType: undefined,
    },
  })

  // Prefill form when data is available from API
  useEffect(() => {
    if (data?.accountType) {
      reset({
        accountType: data.accountType as FormData['accountType'],
      })
    }
  }, [data, reset])

  // Show Community Property option if eligible
  const showCPOption = data?.eligibleAccountTypes.includes(
    JOINT_ACCOUNT_TYPE.COMMUNITY_PROPERTY as JointEnrollmentAccountType,
  )

  // Show Joint Tenants by the Entirety option if eligible
  const showJTBEOption = data?.eligibleAccountTypes.includes(
    JOINT_ACCOUNT_TYPE.JOINT_TENANTS_BY_THE_ENTIRETY as JointEnrollmentAccountType,
  )

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid && formData.accountType) {
      try {
        const mutationData: EnrollmentJointAccountTypeSubmitInput = {
          accountType: formData.accountType,
        }

        const response = await mutate(mutationData)

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        // If path exists and is different
        if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          // Set userId through context update
          setContext({ userId: response.activePerson.applicationId })
          void navigate(response.activePerson.path)
        } else if (!response?.activePerson?.path) {
          // If no path from BE, redirect to error
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentJointLayout title={t('joint.account_type.page_title')}>
      {/* JTWROS MODAL */}
      <ModalDrawer
        isOpen={isJtwrosModalOpen}
        onClose={() => {
          setIsJtwrosModalOpen(false)
        }}
        title={t('joint.account_type.modals.jtwros.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.jtwros.description_1')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.jtwros.description_2')}</li>
          </ul>
        </div>
      </ModalDrawer>
      {/* TIC MODAL */}
      <ModalDrawer
        isOpen={isTicModalOpen}
        onClose={() => {
          setIsTicModalOpen(false)
        }}
        title={t('joint.account_type.modals.tic.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_1')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_2')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_3')}</li>
          </ul>
        </div>
      </ModalDrawer>
      {/* COMMUNITY PROPERTY MODAL */}
      <ModalDrawer
        isOpen={isCommunityPropertyModalOpen}
        onClose={() => {
          setIsCommunityPropertyModalOpen(false)
        }}
        title={t('joint.account_type.modals.community_property.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.community_property.description_1')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.community_property.description_2')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.community_property.description_3')}
            </li>
          </ul>
        </div>
      </ModalDrawer>
      {/* JOINT TENANTS BY THE ENTIRETY MODAL */}
      <ModalDrawer
        isOpen={isJointTenantsEntiretyModalOpen}
        onClose={() => {
          setIsJointTenantsEntiretyModalOpen(false)
        }}
        title={t('joint.account_type.modals.joint_tenants_by_the_entirety.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.joint_tenants_by_the_entirety.description_1')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.joint_tenants_by_the_entirety.description_2')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.joint_tenants_by_the_entirety.description_3')}
            </li>
          </ul>
        </div>
      </ModalDrawer>

      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="account_type-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader title={t('joint.account_type.page_title')} testId="page-header" />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-xl" testId="progress-bar" />
          <p className="typo-primary-base-semibold" data-testid="account-type-title">
            {t('joint.account_type.account_type_title')}
          </p>
          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            <Controller
              name="accountType"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ref: _ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  options={[
                    {
                      value: JOINT_ACCOUNT_TYPE.JTWROS,
                      label: t(`joint.account_type.account_type_input_jtwros.label`),
                      icon: (
                        <InformationCircleIcon
                          className="w-5 h-5 text-text-action stroke-2 cursor-pointer"
                          onClick={() => {
                            setIsJtwrosModalOpen(true)
                          }}
                        />
                      ),
                    },
                    {
                      value: JOINT_ACCOUNT_TYPE.TIC,
                      label: t(`joint.account_type.account_type_input_tic.label`),
                      icon: (
                        <InformationCircleIcon
                          className="w-5 h-5 text-text-action stroke-2 cursor-pointer"
                          onClick={() => {
                            setIsTicModalOpen(true)
                          }}
                        />
                      ),
                    },
                    ...(showCPOption
                      ? [
                          {
                            value: JOINT_ACCOUNT_TYPE.COMMUNITY_PROPERTY,
                            label: t(`joint.account_type.account_type_input_community_property.label`),
                            icon: (
                              <InformationCircleIcon
                                className="w-5 h-5 text-text-action stroke-2 cursor-pointer"
                                onClick={() => {
                                  setIsCommunityPropertyModalOpen(true)
                                }}
                              />
                            ),
                          },
                        ]
                      : []),
                    ...(showJTBEOption
                      ? [
                          {
                            value: JOINT_ACCOUNT_TYPE.JOINT_TENANTS_BY_THE_ENTIRETY,
                            label: t(`joint.account_type.account_type_input_joint_tenants_by_the_entirety.label`),
                            icon: (
                              <InformationCircleIcon
                                className="w-5 h-5 text-text-action stroke-2 cursor-pointer"
                                onClick={() => {
                                  setIsJointTenantsEntiretyModalOpen(true)
                                }}
                              />
                            ),
                          },
                        ]
                      : []),
                  ]}
                  name="accountType"
                  className="!py-xs typo-primary-base"
                  testId="account-type-radio"
                />
              )}
            />

            <div className="flex-1 md:hidden" />

            {/* AUTO GENERATED COMPONENTS */}

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
